/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import { Row, Col } from 'antd';
import styled from 'styled-components';
import parse, {
    attributesToProps,
    HTMLReactParserOptions,
    domToReact,
    DOMNode,
    Element
} from 'html-react-parser';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import * as gtag from 'lib/gtag';
import GalleryCarousel from 'components/GalleryCarousel';
import Headline from './Headline';
import Content from './Content';
import shuffle from 'lib/shuffleArray';
import QuotemarkIcon from 'components/Icons/Quotemark';
import Image from 'components/Image';
import Section, { SectionTitle } from 'components/Section';
import InterestingArticles from './InterestingArticles';
import ArticleImage from 'components/ArticleImage';
import RelatedCompanies from './RelatedCompanies';

const VideoPlayer = dynamic(() => import('components/VideoPlayer'), {
    ssr: false
});

const ExpandNavibox = dynamic(() => import('components/Ads/ExpandNavibox'), {
    ssr: false
});

interface Props {
    article: Article;
    content: string;
    interestingArticles: Article[];
}

const Quote = styled.div`
    font-size: 23px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.52;
    letter-spacing: normal;
    text-align: left;
    color: ${(props) => props.theme.colors.brownishGrey};
`;

const QuotemarkIconStyled = styled(QuotemarkIcon)`
    font-size: 3em;
    color: ${(props) => props.theme.colors.primary};
`;

const parseDimensionToNumber = (value: string): number => {
    if (value.endsWith('%')) {
        return Math.round(885 * (parseInt(value.replace('%', ''), 10) / 100));
    }
    if (value.endsWith('px')) {
        return parseInt(value.replace('px', ''), 10);
    }

    return parseInt(value, 10);
};

function domNodeIsElement(domNode: DOMNode): domNode is Element {
    return domNode.type === 'tag';
}

const parseContent = (content: string, article: Article, interestingArticles: Article[]) => {
    const router = useRouter();
    const options: HTMLReactParserOptions = {
        replace: (domNode: DOMNode) => {
            if (domNodeIsElement(domNode) && domNode.name === 'blockquote') {
                return (
                    <Row wrap={false}>
                        <Col>
                            <QuotemarkIconStyled fill="red" />
                        </Col>
                        <Col flex="auto" style={{ paddingLeft: 10 }}>
                            <Quote>{domToReact(domNode.children as DOMNode[], options)}</Quote>
                        </Col>
                    </Row>
                );
            }
            if (domNodeIsElement(domNode) && domNode.attribs.class === 'article-video') {
                const placeholder = domNode.attribs['aria-placeholder'];
                const url = domNode.attribs['aria-valuetext'];
                if (!placeholder || !url) return;
                return (
                    <Section>
                        <Col span={24}>
                            <VideoPlayer
                                light={placeholder}
                                url={url}
                                onStart={() => {
                                    gtag.event({
                                        action: 'Play',
                                        category: 'Videos',
                                        label: placeholder,
                                        value: router.asPath
                                    });
                                }}
                            />
                        </Col>
                    </Section>
                );
            }
            if (domNodeIsElement(domNode) && domNode.attribs.class === 'article-ad navibox') {
                return (
                    <ExpandNavibox id={domNode.attribs['aria-placeholder']} margin="25px auto" />
                );
            }

            if (domNodeIsElement(domNode) && domNode.attribs.class === 'article-gallery') {
                if (!article.gallery) {
                    return;
                }
                return (
                    <Section>
                        <Col>
                            <SectionTitle title="Galeria artykułu" />
                        </Col>
                        <Col span={24}>
                            <GalleryCarousel itemsPerRow={3} gallery={article.gallery} />
                        </Col>
                    </Section>
                );
            }

            if (
                domNodeIsElement(domNode) &&
                domNode.attribs.class === 'article-interesting-articles'
            ) {
                if (interestingArticles.length === 0) {
                    return;
                }
                return (
                    <Section
                        key={`paragraph-interesting-article`}
                        style={{
                            backgroundColor: '#f4f4f4',
                            padding: '15px 10px',
                            margin: '35px 5px'
                        }}
                        gutter={16}
                        align="middle"
                    >
                        <Col span={24} style={{ paddingTop: 15 }}>
                            <SectionTitle title="MOŻE ZAINTERESUJE CIĘ TAKŻE" level={3} />
                        </Col>
                        <Col span={24}>
                            <InterestingArticles interestingArticles={interestingArticles} />
                        </Col>
                    </Section>
                );
            }

            if (
                domNodeIsElement(domNode) &&
                domNode.attribs.class === 'article-related-companies'
            ) {
                const shuffledCompanies: Company[] = useMemo(
                    () =>
                        shuffle<Company>(
                            article.companies.filter((company: Company): boolean => company.premium)
                        ),
                    [article.seoSlug, article.companies.length]
                );

                if (shuffledCompanies.length === 0) {
                    return;
                }

                return (
                    <Row>
                        <Col span={24}>
                            <Row style={{ margin: 25 }} key={`paragraph-related-companies`}>
                                <Col span={24}>
                                    <RelatedCompanies companies={shuffledCompanies} />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                );
            }

            if (domNodeIsElement(domNode) && domNode.name === 'figure') {
                const imageNode = domNode.children.find((node: Element) => {
                    return node.name === 'img';
                }) as Element;
                if (!imageNode) {
                    return;
                }

                const captionNode = domNode.children.find((node: Element) => {
                    return node.name === 'figcaption';
                }) as Element;

                const getCopyright = (node: Element): string | null => {
                    if (node?.children && node.children.length > 0) {
                        return (node.children[0] as unknown as any).data;
                    }

                    return;
                };

                const copyright = getCopyright(captionNode);

                const getCaption = (node: Element): string | null => {
                    const isCaptionDefined = (): boolean =>
                        node?.children &&
                        node.children.length > 0 &&
                        (node.children[0] as Element) &&
                        (node.children[0] as Element).children &&
                        (node.children[0] as Element).children.length > 0;

                    if (isCaptionDefined()) {
                        return ((node.children[0] as Element).children[0] as unknown as any).data;
                    }

                    return;
                };
                const caption = getCaption(captionNode);

                const domAttribs = attributesToProps(domNode.attribs);
                const { src, alt, width, height, ...restProps } = attributesToProps(
                    imageNode.attribs
                );
                if (!src || typeof src != 'string') {
                    return <div></div>;
                }
                return (
                    <ArticleImage
                        type="original"
                        image={src}
                        alt={alt as string}
                        width={parseDimensionToNumber(width as string)}
                        height={parseDimensionToNumber(height as string)}
                        copyright={copyright}
                        caption={caption}
                        margin="0 20px"
                        imageProps={restProps}
                        {...domAttribs}
                    />
                );
            }

            if (domNodeIsElement(domNode) && domNode.name === 'img') {
                const { src, alt, width, height, ...restProps } = attributesToProps(
                    domNode.attribs
                );
                if (!src) {
                    return;
                }

                if (!width || !height) {
                    return (
                        <Image
                            type="original"
                            width={885}
                            height={500}
                            alt={(alt as string) || `${article.title} zdjęcie w treści artykułu`}
                            src={src as string}
                            {...restProps}
                        />
                    );
                }
                const isFilled = width === '100%' || height === '100%';

                return (
                    <Image
                        alt={(alt as string) || `${article.title} zdjęcie w treści artykułu`}
                        type="original"
                        fill={isFilled}
                        src={src as string}
                        width={parseDimensionToNumber(width as string)}
                        height={parseDimensionToNumber(height as string)}
                        {...restProps}
                    />
                );
            }
        }
    };
    return parse(content, options);
};

const ArticleContent = ({ article, content, interestingArticles }: Props): JSX.Element => {
    return (
        <>
            <Headline dangerouslySetInnerHTML={{ __html: article.headline }} />
            <Row>
                <Content span={24}>{parseContent(content, article, interestingArticles)}</Content>
            </Row>
        </>
    );
};

export default ArticleContent;
